import { ConnectorPropertyColumn } from '@/enums/tables/connectorPropertyColumn.enum';
import { ConnectornHelper } from '@/helpers/connector.helper';
import { MatHelper } from '@/helpers/mat-helper.helper';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { AuthenticationService } from '@/services/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import {
  ConnectorCode,
  ConnectorConfig,
  ConnectorProperty,
  ConnectorUser,
  ConnectorsService,
  ErrorBusinessCode,
  PropertiesService,
  RealProperty,
  UserSession
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { coreEnvironment } from 'src/environments/core/core-environment';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ConnectorConfigDialogComponent } from '../connector-config-dialog/connector-config-dialog.component';
import { ConnectorSyncDialogComponent } from '../connector-sync-dialog/connector-sync-dialog.component';
import { InfoDialogComponent } from '../info-dialog/info-dialog.component';
import { PropertyAddFullDialogComponent } from '../property-add-full-dialog/property-add-full-dialog.component';
import { FullAddressPipe } from '../../extensions/full-address.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatMenuModule } from '@angular/material/menu';
import { RouterLink } from '@angular/router';
import { ImgDisplayComponent } from '../img-display/img-display.component';
import { MatIconModule } from '@angular/material/icon';

import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';

export interface IConnectorProperty extends ConnectorProperty {
  badge?: string;
  statusText?: string;
  statusIcon?: string;
  statusColor?: string;
  eemoProp?: RealProperty;
}

@Component({
    selector: 'app-connector-user',
    templateUrl: './connector-user.component.html',
    styleUrls: ['./connector-user.component.scss'],
    animations: MatHelper.getTableAnimations(),
    standalone: true,
    imports: [NgIf, MatButtonModule, MatIconModule, MatTableModule, ImgDisplayComponent, TooltipDirective, RouterLink, MatMenuModule, MatProgressSpinnerModule, TranslateModule, FullAddressPipe]
})
export class ConnectorUserComponent implements OnInit, OnDestroy {
  @Input() code: ConnectorCode;

  currentUser: UserSession;
  canSync: boolean;
  connectorUser: ConnectorUser;
  connectorConfig: ConnectorConfig;

  connectorSub = new Subscription();
  getListSub = new Subscription();
  startSyncSub = new Subscription();
  deleteSub = new Subscription();
  getConfigSub = new Subscription();
  getPropSub = new Subscription();

  connectorLogoSrc: string;
  connectorLogoSrcSet: string;
  connectorDisplayName: string;

  dataSource: MatTableDataSource<IConnectorProperty>;
  isLoading = false;
  isError = false;
  isErrorCredentials = false;
  COLUMNS_ID = ConnectorPropertyColumn;
  displayedColumns = coreEnvironment.tables.connectorProperty.lightColumns;

  configInitialized = false;

  constructor(
    private appConfigService: AppConfigService,
    private connectorHelper: ConnectornHelper,
    private connectorService: ConnectorsService,
    private dialog: MatDialog,
    private authenticationService: AuthenticationService,
    private alertService: AlertService,
    private propertyHelper: PropertyHelper,
    private propertiesService: PropertiesService,
    private userHelper: UserHelper
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authenticationService.currentUserValue;
    this.canSync = this.userHelper.isCurrentSubscriptionActive();
    this.connectorSub = this.appConfigService.availableConnectorsObs.subscribe((connectors) => {
      this.connectorUser = connectors.find((c) => c.code === this.code);
      this.connectorLogoSrc = this.connectorHelper.getConnectorLogo(this.connectorUser.code);
      this.connectorLogoSrcSet = this.connectorHelper.getConnectorLogo2x(this.connectorUser.code) + ' 2x';
    });
    this.connectorDisplayName = this.connectorHelper.getConnectorDisplayName(this.code);
    this.getConnectorConfig();
  }

  ngOnDestroy(): void {
    this.connectorSub.unsubscribe();
    this.getListSub.unsubscribe();
    this.startSyncSub.unsubscribe();
    this.deleteSub.unsubscribe();
    this.getConfigSub.unsubscribe();
    this.getPropSub.unsubscribe();
  }

  afterGetConfig(config: ConnectorConfig) {
    this.connectorConfig = config;
    this.configInitialized = true;
    this.loadData();
  }

  afterGetLoadData(propList: ConnectorProperty[]) {
    const list: IConnectorProperty[] = propList;
    list.forEach((p) => {
      // convert date to allow the comparison
      p.lastSyncDate = new Date(p.lastSyncDate);
      p.lastUpdateDate = new Date(p.lastUpdateDate);
      p.badge = this.propertyHelper.getBadgeImageName(p.type);
      if (p.synced) {
        if (p.lastUpdateDate > p.lastSyncDate) {
          p.statusText = 'Des modifications sont à synchroniser';
          p.statusIcon = 'published_with_changes';
          p.statusColor = 'warn';
        } else {
          p.statusText = 'Synchronisé';
          p.statusIcon = 'check_circle_outline';
          p.statusColor = 'accent';
        }
      } else {
        p.statusText = 'A synchroniser';
        p.statusIcon = 'cloud_download';
        p.statusColor = 'accent';
      }
      if (p.eemoUid) {
        this.getPropSub = this.propertiesService.getRealProperty(p.eemoUid).subscribe((prop) => (p.eemoProp = prop));
      }
    });
    this.dataSource = new MatTableDataSource(list);
  }

  configureConnector() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConnectorConfigDialogComponent, {
        data: { connectorCode: this.code }
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.getConnectorConfig();
          this.alertService.success('Configuration mise à jour');
        }
      });
    }
  }

  confirmDelete() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: 'Confirmez-vous la suppression du connecteur ?'
        }
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDelete();
        }
      });
    }
  }

  doDelete() {
    this.deleteSub = this.connectorService.deleteConnectorUserConfig(this.currentUser.userId, this.code).subscribe({
      next: () => {
        this.connectorConfig = null;
        this.alertService.success('Configuration supprimée');
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  goStartSync(prop: IConnectorProperty) {
    if (!this.canSync) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        maxWidth: '30rem',
        data: {
          title: 'Synchronisation désactivée',
          content: '<p>Votre abonnement doit être actif pour synchroniser un bien.</p>'
        }
      });
      return;
    }
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConnectorSyncDialogComponent, {
        data: {
          userId: this.currentUser.userId,
          connectorCode: this.code,
          connectorProperty: prop
        }
      });
      dialogRef.afterClosed().subscribe((propUid) => {
        this.loadData();
        if (propUid) {
          this.getPropSub = this.propertiesService
            .getRealProperty(propUid)
            .subscribe((prop) => this.goConfigureProperty(prop));
        }
      });
    }
  }

  goConfigureProperty(prop: RealProperty) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(PropertyAddFullDialogComponent, {
        data: {
          existingProp: prop
        }
      });
      dialogRef.afterClosed().subscribe(() => {
        this.loadData();
      });
    }
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  loadData() {
    if (!this.connectorConfig) {
      return;
    }
    this.isLoading = true;
    this.getListSub = this.connectorService.getConnectorUserPropertyList(this.currentUser.userId, this.code).subscribe({
      next: (propList: ConnectorProperty[]) => {
        this.isLoading = false;
        this.isError = false;
        this.isErrorCredentials = false;
        this.afterGetLoadData(propList);
      },
      error: (err: HttpErrorResponse) => {
        this.isError = true;
        if (
          err.error &&
          err.error.businessCode &&
          err.error.businessCode === ErrorBusinessCode.CONNECTOR_INVALID_CREDENTIALS
        ) {
          this.isErrorCredentials = true;
          this.alertService.warning(
            'Le serveur ' + this.connectorDisplayName + ' a refusé la connexion: identifiants invalides.'
          );
        }
        this.isLoading = false;
      }
    });
  }

  getConnectorConfig() {
    this.isLoading = true;
    this.getConfigSub = this.connectorService.getConnectorUserConfig(this.currentUser.userId, this.code).subscribe({
      next: (currentConfig: ConnectorConfig) => {
        this.isLoading = false;
        this.afterGetConfig(currentConfig);
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }
}
